// Service layer for incident email notification settings
// CREATED: Handles CRUD for incident_email_settings table and edge function call for notifications
// Exports: getIncidentEmailSettings, updateIncidentEmailSettings, sendIncidentNotification

import { supabase } from './supabase';
import { envConfig } from './envConfig';
import type { Incident } from './incidents';
import { captureException } from './sentry';

export interface IncidentEmailSetting {
  id: string;
  category: Incident['category'];
  is_enabled: boolean;
  email_addresses: string[];
  updated_at: string;
}

export interface IncidentEmailSettingUpdate {
  category: Incident['category'];
  is_enabled: boolean;
  email_addresses: string[];
}

export async function getIncidentEmailSettings(): Promise<{
  success: boolean;
  data?: IncidentEmailSetting[];
  error?: string;
}> {
  try {
    const { data, error } = await supabase
      .from('incident_email_settings')
      .select('id, category, is_enabled, email_addresses, updated_at')
      .order('category');

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true, data: data as IncidentEmailSetting[] };
  } catch (err) {
    if (import.meta.env.DEV) console.error('[getIncidentEmailSettings] Error:', err);
    captureException(err, { context: 'getIncidentEmailSettings' });
    return { success: false, error: 'Une erreur est survenue lors du chargement' };
  }
}

export async function updateIncidentEmailSettings(
  settings: IncidentEmailSettingUpdate[]
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('incident_email_settings')
      .upsert(
        settings.map(s => ({
          category: s.category,
          is_enabled: s.is_enabled,
          email_addresses: s.email_addresses,
        })),
        { onConflict: 'category' }
      );

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (err) {
    if (import.meta.env.DEV) console.error('[updateIncidentEmailSettings] Error:', err);
    captureException(err, { context: 'updateIncidentEmailSettings' });
    return { success: false, error: 'Une erreur est survenue lors de l\'enregistrement' };
  }
}

export function sendIncidentNotification(incidentId: string): void {
  (async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const url = `${envConfig.supabaseUrl}/functions/v1/send-incident-notification`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
          'Apikey': envConfig.supabaseAnonKey,
        },
        body: JSON.stringify({ incident_id: incidentId }),
      });

      if (!response.ok) {
        const text = await response.text();
        if (import.meta.env.DEV) console.error('[sendIncidentNotification] Error response:', response.status, text);
        const bodyPreview = text ? text.slice(0, 200) + (text.length > 200 ? '\u2026(truncated)' : '') : '';
        captureException(new Error(`sendIncidentNotification HTTP ${response.status}`), { context: 'sendIncidentNotification', status: response.status, bodyPreview, bodyLength: text?.length ?? 0 });
      }
    } catch (err) {
      if (import.meta.env.DEV) console.error('[sendIncidentNotification] Network error:', err);
      captureException(err, { context: 'sendIncidentNotification.network' });
    }
  })();
}
